'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useConnection, useDisconnect, useReconnect } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useLocation } from 'react-router';
import { Wallet, RefreshCw, AlertTriangle } from 'lucide-react';

interface WalletConnectionGuardProps {
  children: React.ReactNode;
}

// Helper function to check if there's stored wallet connection data
function hasStoredWalletConnection(): boolean {
  try {
    // Check wagmi storage
    const stored = localStorage.getItem('wagmi');
    if (stored) {
      const parsed = JSON.parse(stored);
      // wagmi stores connections as an array in the serialized format
      // Check for current connection or connections array with items
      if (parsed?.state?.current) {
        return true;
      }
      // connections might be serialized differently - check if it exists and has data
      if (parsed?.state?.connections) {
        // If it's an array, check length
        if (Array.isArray(parsed.state.connections) && parsed.state.connections.length > 0) {
          return true;
        }
        // If it's an object (serialized Map), check if it has entries
        if (typeof parsed.state.connections === 'object') {
          const keys = Object.keys(parsed.state.connections);
          if (keys.length > 0) {
            return true;
          }
        }
      }
    }

    // Also check for recentConnectorId which indicates previous connection
    const recentConnector = localStorage.getItem('wagmi.recentConnectorId');
    if (recentConnector) {
      return true;
    }
  } catch {
    // Ignore parsing errors
  }
  return false;
}

function getStoredConnectorId(): string | null {
  try {
    return localStorage.getItem('wagmi.recentConnectorId');
  } catch {
    return null;
  }
}

function clearStoredWalletConnection(): void {
  try {
    localStorage.removeItem('wagmi');
    localStorage.removeItem('wagmi.recentConnectorId');
  } catch {
    // Ignore storage errors
  }
}

export function WalletConnectionGuard({ children }: WalletConnectionGuardProps) {
  const { isConnected, isConnecting, isReconnecting, status } = useConnection();
  const { openConnectModal } = useConnectModal();
  const { mutate: disconnect } = useDisconnect();
  const { reconnectAsync, connectors } = useReconnect();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const wasConnectedRef = useRef(false);
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriedReconnectRef = useRef(false);

  const clearInitTimeout = useCallback(() => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
  }, []);

  const resetStoredConnection = useCallback(() => {
    clearStoredWalletConnection();
    hasTriedReconnectRef.current = false;
  }, []);

  // Public paths that don't require wallet connection
  const publicPaths = ['/login', '/'];
  const isPublicPath = publicPaths.includes(location.pathname);

  // Track if wagmi is still working (connecting/reconnecting)
  const isWagmiWorking =
    status === 'connecting' || status === 'reconnecting' || isConnecting || isReconnecting;

  // Handle successful connection
  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      setShowModal(false);
      setIsInitializing(false);
      hasTriedReconnectRef.current = false;

      // Clear any pending timeout
      clearInitTimeout();
    }
  }, [isConnected, clearInitTimeout]);

  // Initial mount - handle reconnection
  useEffect(() => {
    // Skip for public paths
    if (isPublicPath) {
      setIsInitializing(false);
      return;
    }

    // Already connected
    if (isConnected) {
      setIsInitializing(false);
      return;
    }

    // wagmi is still working, wait for it
    if (isWagmiWorking) {
      return;
    }

    // Check for stored connection and try reconnect
    const hadStoredConnection = hasStoredWalletConnection();
    const storedConnectorId = getStoredConnectorId();
    const matchingConnectors = storedConnectorId
      ? connectors.filter(connector => connector.id === storedConnectorId)
      : connectors;

    if (hadStoredConnection && !hasTriedReconnectRef.current && matchingConnectors.length > 0) {
      hasTriedReconnectRef.current = true;
      reconnectAsync({ connectors: matchingConnectors })
        .catch(() => {
          resetStoredConnection();
          clearInitTimeout();
          setIsInitializing(false);
          setShowModal(true);
        })
        .finally(() => {
          // Allow future reconnect attempts after a failure
          if (!isConnected) {
            hasTriedReconnectRef.current = false;
          }
        });
      // Give wagmi time to process the reconnect
      return;
    }

    // Set a timeout to end initialization period
    // Only if we haven't set one already
    if (!initTimeoutRef.current) {
      initTimeoutRef.current = setTimeout(() => {
        setIsInitializing(false);
        initTimeoutRef.current = null;

        // After initialization, if still not connected and had stored connection, show modal
        if (hadStoredConnection) {
          setShowModal(true);
        }
      }, 5000);
    }

    return () => {
      clearInitTimeout();
    };
  }, [
    isConnected,
    isWagmiWorking,
    isPublicPath,
    connectors,
    reconnectAsync,
    resetStoredConnection,
    clearInitTimeout,
  ]);

  // Handle disconnect during active session (not on initial load)
  useEffect(() => {
    if (isInitializing || isPublicPath) {
      return;
    }

    // User was connected but now disconnected
    if (wasConnectedRef.current && !isConnected && !isWagmiWorking) {
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isConnected, isWagmiWorking, isInitializing, isPublicPath]);

  // Simple retry - just open the connect modal directly
  // This works reliably for both desktop (MetaMask extension) and mobile (WalletConnect)
  const handleRetryConnect = useCallback(() => {
    resetStoredConnection();
    disconnect();
    openConnectModal?.();
    setShowModal(false);
  }, [openConnectModal, resetStoredConnection, disconnect]);

  const handleOpenConnectModal = useCallback(() => {
    resetStoredConnection();
    disconnect();
    openConnectModal?.();
    setShowModal(false);
  }, [openConnectModal, resetStoredConnection, disconnect]);

  // Show loading during initialization (only on protected paths with stored connection)
  if (isInitializing && !isPublicPath && (isConnecting || isReconnecting)) {
    return (
      <>
        {children}
        <div className='fixed inset-0 z-[9999] flex items-center justify-center'>
          <div className='absolute inset-0 bg-black/40 backdrop-blur-sm' />
          <div className='relative z-10 bg-white rounded-2xl p-8 shadow-xl flex flex-col items-center gap-4'>
            <RefreshCw size={32} className='text-teal-600 animate-spin' />
            <p className='text-gray-700 font-medium'>Reconnecting wallet...</p>
          </div>
        </div>
      </>
    );
  }

  // Don't show modal on public paths or when connected
  if (isPublicPath || isConnected) {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Wallet Disconnected Modal */}
      {showModal && (
        <div className='fixed inset-0 z-[9999] flex items-center justify-center'>
          {/* Blur Backdrop */}
          <div
            className='absolute inset-0 bg-black/60 backdrop-blur-md'
            onClick={e => e.stopPropagation()}
          />

          {/* Modal Content */}
          <div className='relative z-10 w-[90%] max-w-md mx-auto'>
            <div className='bg-white rounded-3xl shadow-2xl overflow-hidden'>
              {/* Header with gradient */}
              <div className='bg-gradient-to-r from-orange-500 to-red-500 px-6 py-8 text-center'>
                <div className='w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm'>
                  <AlertTriangle size={40} className='text-white' />
                </div>
                <h2 className='text-2xl font-bold text-white mb-2'>Wallet Disconnected</h2>
                <p className='text-white/90 text-sm'>
                  Your wallet connection was lost. Please reconnect to continue.
                </p>
              </div>

              {/* Body */}
              <div className='px-6 py-6 space-y-4'>
                <div className='bg-amber-50 border border-amber-200 rounded-xl p-4'>
                  <p className='text-sm text-amber-800'>
                    This may happen due to network issues, wallet app being closed, or session
                    expiry.
                  </p>
                </div>

                {/* Retry Button */}
                <button
                  onClick={handleRetryConnect}
                  disabled={isConnecting || isReconnecting}
                  className='w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-3'
                >
                  {isConnecting || isReconnecting ? (
                    <>
                      <RefreshCw size={20} className='animate-spin' />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <Wallet size={20} />
                      <span>Connect Wallet</span>
                    </>
                  )}
                </button>

                {/* Connect Different Wallet Button */}
                <button
                  onClick={handleOpenConnectModal}
                  disabled={isConnecting || isReconnecting}
                  className='w-full bg-white border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 text-gray-700 font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3'
                >
                  <Wallet size={20} />
                  <span>Connect Different Wallet</span>
                </button>

                {/* Info text */}
                <p className='text-xs text-gray-500 text-center'>
                  Make sure your wallet app is open and unlocked
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WalletConnectionGuard;
